import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Gauge, 
    Fuel, 
    Navigation, 
    TrendingUp,
    Filter,
    Ship,
    User,
    MapPin,
    Download
} from 'lucide-react';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    LineChart,
    Line,
    Legend
} from 'recharts';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const DashboardOperacionalPage = () => {
    const [data, setData] = useState(null);
    const [empurradores, setEmpurradores] = useState([]);
    const [destinos, setDestinos] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [filters, setFilters] = useState({
        data_inicio: '',
        data_fim: '',
        empurrador_id: '',
        comandante: '',
        destino_id: ''
    });

    useEffect(() => {
        fetchBaseData();
        fetchDashboard();
    }, []);

    const fetchBaseData = async () => {
        try {
            const [empRes, destRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/destinos`)
            ]);
            setEmpurradores(empRes.data);
            setDestinos(destRes.data);
        } catch (error) {
            console.error('Error fetching base data:', error);
        }
    };

    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.data_inicio) params.data_inicio = filters.data_inicio;
            if (filters.data_fim) params.data_fim = filters.data_fim;
            if (filters.empurrador_id && filters.empurrador_id !== 'all') params.empurrador_id = filters.empurrador_id;
            if (filters.comandante) params.comandante = filters.comandante;
            if (filters.destino_id && filters.destino_id !== 'all') params.destino_id = filters.destino_id;

            const response = await axios.get(`${API}/dashboard/operacional`, { params });
            setData(response.data);
        } catch (error) {
            toast.error('Erro ao carregar dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        fetchDashboard();
    };

    const exportCSV = () => {
        if (!data) return;
        
        let csv = 'Métrica,Valor\n';
        csv += `Total de Viagens,${data.total_viagens}\n`;
        csv += `Média Consumo/Hora (L/h),${data.media_consumo_hora}\n`;
        csv += `Consumo Total (L),${data.consumo_total}\n`;
        csv += `Média Velocidade (nós),${data.media_velocidade}\n`;
        csv += `Média L/Milha,${data.media_litros_milha}\n\n`;
        
        csv += '\nPor Empurrador\n';
        csv += 'Nome,Viagens,Consumo Total,Média Consumo/h,Média Velocidade,Média L/Milha\n';
        data.por_empurrador.forEach(e => {
            csv += `${e.nome},${e.viagens},${e.consumo_total},${e.media_consumo_hora},${e.media_velocidade},${e.media_litros_milha}\n`;
        });
        
        csv += '\nPor Comandante\n';
        csv += 'Comandante,Viagens,Consumo Total,Média Consumo/h,Média Velocidade\n';
        data.por_comandante.forEach(c => {
            csv += `${c.comandante},${c.viagens},${c.consumo_total},${c.media_consumo_hora},${c.media_velocidade}\n`;
        });
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dashboard_operacional_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        
        toast.success('Relatório exportado!');
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando dashboard...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard-operacional-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Dashboard Operacional
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Indicadores de performance das viagens
                    </p>
                </div>
                
                <Button 
                    onClick={exportCSV}
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                >
                    <Download className="w-4 h-4 mr-2" />
                    Exportar CSV
                </Button>
            </div>

            {/* Filters */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter className="w-4 h-4 text-cyan-400" />
                        <span className="text-sm font-mono uppercase tracking-wider text-cyan-400">Filtros</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Início</Label>
                            <Input
                                type="date"
                                value={filters.data_inicio}
                                onChange={(e) => setFilters({...filters, data_inicio: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Data Fim</Label>
                            <Input
                                type="date"
                                value={filters.data_fim}
                                onChange={(e) => setFilters({...filters, data_fim: e.target.value})}
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Empurrador</Label>
                            <Select 
                                value={filters.empurrador_id || "all"} 
                                onValueChange={(value) => setFilters({...filters, empurrador_id: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {empurradores.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id}>{emp.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-slate-400 text-xs">Destino</Label>
                            <Select 
                                value={filters.destino_id || "all"} 
                                onValueChange={(value) => setFilters({...filters, destino_id: value === "all" ? "" : value})}
                            >
                                <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-900 border-slate-800">
                                    <SelectItem value="all">Todos</SelectItem>
                                    {destinos.map((dest) => (
                                        <SelectItem key={dest.id} value={dest.id}>{dest.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-end">
                            <Button 
                                onClick={handleFilter}
                                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-9"
                            >
                                Filtrar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-4 text-center">
                        <Navigation className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{data?.total_viagens || 0}</p>
                        <p className="text-xs text-slate-400 uppercase mt-1">Total Viagens</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-4 text-center">
                        <Fuel className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{data?.media_consumo_hora || 0}</p>
                        <p className="text-xs text-slate-400 uppercase mt-1">Média L/h</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-4 text-center">
                        <Fuel className="w-6 h-6 text-red-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{data?.consumo_total?.toLocaleString('pt-BR') || 0}</p>
                        <p className="text-xs text-slate-400 uppercase mt-1">Consumo Total (L)</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-4 text-center">
                        <Gauge className="w-6 h-6 text-green-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{data?.media_velocidade || 0}</p>
                        <p className="text-xs text-slate-400 uppercase mt-1">Vel. Média (nós)</p>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardContent className="p-4 text-center">
                        <TrendingUp className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-white">{data?.media_litros_milha || 0}</p>
                        <p className="text-xs text-slate-400 uppercase mt-1">Média L/Milha</p>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Por Empurrador */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="pb-0">
                        <CardTitle className="text-white text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                            <Ship className="w-4 h-4 text-cyan-400" />
                            Consumo por Empurrador
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {data?.por_empurrador?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={data.por_empurrador}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis 
                                        dataKey="nome" 
                                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#1e293b', 
                                            border: '1px solid #334155',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <Bar dataKey="media_consumo_hora" fill="#06b6d4" name="Consumo (L/h)" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-slate-500">
                                Sem dados para exibir
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Velocidade por Empurrador */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader className="pb-0">
                        <CardTitle className="text-white text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                            <Gauge className="w-4 h-4 text-green-400" />
                            Velocidade Média por Empurrador
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {data?.por_empurrador?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={data.por_empurrador}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis 
                                        dataKey="nome" 
                                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#1e293b', 
                                            border: '1px solid #334155',
                                            borderRadius: '4px'
                                        }}
                                    />
                                    <Bar dataKey="media_velocidade" fill="#22c55e" name="Velocidade (nós)" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="h-[250px] flex items-center justify-center text-slate-500">
                                Sem dados para exibir
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Por Comandante */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-white text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                            <User className="w-4 h-4 text-yellow-400" />
                            Performance por Comandante
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {data?.por_comandante?.length > 0 ? (
                            <Table className="table-naval">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Comandante</TableHead>
                                        <TableHead className="text-right">Viagens</TableHead>
                                        <TableHead className="text-right">Consumo (L/h)</TableHead>
                                        <TableHead className="text-right">Vel. Média</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.por_comandante.map((cmd, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="text-white">{cmd.comandante}</TableCell>
                                            <TableCell className="text-right text-slate-400">{cmd.viagens}</TableCell>
                                            <TableCell className="text-right text-orange-400 font-mono">{cmd.media_consumo_hora}</TableCell>
                                            <TableCell className="text-right text-green-400 font-mono">{cmd.media_velocidade}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="p-8 text-center text-slate-500">Sem dados</div>
                        )}
                    </CardContent>
                </Card>

                {/* Por Destino */}
                <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-white text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-cyan-400" />
                            Viagens por Destino
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {data?.por_destino?.length > 0 ? (
                            <Table className="table-naval">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Destino</TableHead>
                                        <TableHead className="text-right">Viagens</TableHead>
                                        <TableHead className="text-right">Consumo Total (L)</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.por_destino.map((dest, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="text-white">{dest.nome}</TableCell>
                                            <TableCell className="text-right text-slate-400">{dest.viagens}</TableCell>
                                            <TableCell className="text-right text-orange-400 font-mono">{dest.consumo_total?.toLocaleString('pt-BR')}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="p-8 text-center text-slate-500">Sem dados</div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default DashboardOperacionalPage;
